import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import { MdVisibility } from 'react-icons/md';
import { getCustomerViewedProducts, getCustomerOrderedProductIds } from 'interfaces/customer';
import { getProductsByIds } from 'interfaces/products';

interface ViewedProduct {
    _id: string;
    name: string;
    ref?: string;
    unity?: string;
    viewCount: number;
    lastViewedAt: string;
    alreadyOrdered: boolean;
}

export default function ViewedProducts(props: { customerId: string }) {
    const { customerId } = props;
    const [viewedProducts, setViewedProducts] = useState<ViewedProduct[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchViewedProducts = async () => {
        setLoading(true);
        const [res, orderedProductIdsList] = await Promise.all([
            getCustomerViewedProducts(customerId),
            getCustomerOrderedProductIds(customerId),
        ]);
        const views: { productId: string; viewCount: number; lastViewedAt: string }[] =
            res?.viewedProducts || [];
        const orderedProductIds = new Set(orderedProductIdsList);

        if (views.length > 0) {
            const details = await getProductsByIds(views.map((v) => v.productId));
            const detailsById: Record<string, any> = {};
            (details || []).forEach((p: any) => {
                detailsById[p._id] = p;
            });

            // Keep the backend's most-recent-first order; drop entries whose
            // product was since deleted.
            const merged = views
                .map((v) => {
                    const product = detailsById[v.productId];
                    if (!product) return null;
                    return {
                        _id: product._id,
                        name: product.name,
                        ref: product.ref,
                        unity: product.unity,
                        viewCount: v.viewCount,
                        lastViewedAt: v.lastViewedAt,
                        alreadyOrdered: orderedProductIds.has(product._id),
                    };
                })
                .filter(Boolean) as ViewedProduct[];

            setViewedProducts(merged);
        } else {
            setViewedProducts([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchViewedProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return (
        <Card extra="w-full !p-4 border col-span-12 sm:col-span-6">
            <div className="flex items-center gap-2 mb-3">
                <MdVisibility className="text-blue-500" />
                <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                    Produits consultés
                </h4>
                <span className="ml-auto text-xs text-gray-400">
                    {viewedProducts.length} produit{viewedProducts.length > 1 ? 's' : ''}
                </span>
            </div>

            {loading ? (
                <p className="text-sm text-gray-400">Chargement...</p>
            ) : viewedProducts.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                    Ce client n'a encore consulté aucun produit sur sa boutique B2B.
                </p>
            ) : (
                <div className="flex flex-col gap-2">
                    {viewedProducts.map((product) => (
                        <div
                            key={product._id}
                            className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5"
                        >
                            <div className="flex items-center gap-1.5 min-w-0">
                                <MdVisibility className="text-blue-400 flex-shrink-0" size={12} />
                                <span className="text-xs font-medium text-blue-800 truncate">
                                    {product.name}
                                </span>
                                {product.ref && (
                                    <span className="text-xs text-blue-400 flex-shrink-0">
                                        ({product.ref})
                                    </span>
                                )}
                                <span
                                    className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                        product.alreadyOrdered
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-amber-100 text-amber-700'
                                    }`}
                                >
                                    {product.alreadyOrdered ? 'Déjà commandé' : 'Jamais commandé'}
                                </span>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <span className="text-xs text-blue-700 font-semibold">
                                    {product.viewCount} vue{product.viewCount > 1 ? 's' : ''}
                                </span>
                                <span className="block text-[11px] text-blue-400">
                                    {new Date(product.lastViewedAt).toLocaleDateString('fr-FR')}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}
